import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ForumSeo } from '@/components/ForumSeo'
import { forumCard, forumCardHeader, forumKicker, forumLink, forumMuted, forumTitleLg } from '@/lib/forumUi'
import { GUIDELINES_SEO_DESCRIPTION, GUIDELINES_SEO_TITLE } from '~shared/seo'

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="border-t border-zinc-100 px-5 py-5 md:px-6 md:py-6">
      <h2 id={id} className="border-b border-zinc-100 pb-2 text-lg font-semibold tracking-tight text-zinc-950 md:text-xl">
        {title}
      </h2>
      <div className={`${forumMuted} mt-3`}>{children}</div>
    </section>
  )
}

export function GuidelinesPage() {
  return (
    <article className={forumCard}>
      <ForumSeo title={GUIDELINES_SEO_TITLE} description={GUIDELINES_SEO_DESCRIPTION} />
      <header className={forumCardHeader}>
        <p className={forumKicker}>Community</p>
        <h1 className={forumTitleLg}>Forum guidelines</h1>
        <p className={`${forumMuted} mt-2`}>
          NK Our Town is for useful, civil discussion grounded in North Kingstown: news, civic life, schools, and neighborhoods. These rules
          exist to keep posts readable for everyone.
        </p>
      </header>

      <Section id="g1" title="1. Cite sources">
        When posting news or claims, link to primary sources where possible and say where information came from. Unsourced rumor may be removed.
      </Section>

      <Section id="g2" title="2. Local commerce">
        Buy/sell and service posts must be truthful and straightforward (condition, price, and contact expectation). Spam and MLM style posts do not belong here.
      </Section>

      <Section id="g3" title="3. Protect kids and staff">
        Do not identify individual students by name alongside sensitive topics. Be respectful toward schools and public employees; criticize policies and
        decisions, not people’s private lives.
      </Section>

      <Section id="g4" title="4. Moderation">
        Moderators may pin, lock, hide, or remove threads and posts that break these guidelines or degrade the forum. Admins assign categories and roles.
        Disagreements with moderation should stay civil; use appropriate boards for meta discussion when available.
      </Section>

      <section className="border-t border-amber-200/80 bg-amber-50/90 px-5 py-4 text-[14px] leading-relaxed text-amber-950 md:px-6">
        Nothing here is legal advice or official town communication. Check{' '}
        <a className={forumLink} href="https://www.northkingstown.org/">
          northkingstown.org
        </a>{' '}
        for municipal information.
      </section>

      <footer className="border-t border-zinc-100 bg-zinc-50/80 px-5 py-4 text-[14px] text-zinc-600 md:px-6">
        <Link className={forumLink} to="/sign-up">
          Register
        </Link>
        <span className="text-zinc-400"> · </span>
        <Link className={forumLink} to="/discussions">
          All discussions
        </Link>
      </footer>
    </article>
  )
}
